import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Button, CopyableText, Heading, styles, Text, Truncated } from '@frontend/design-system';
import { useScheduleAppointmentInfoPanelShallowStore } from '../../../stores/schedule-appointment-info-panel.store';
import { AppointmentEventActionButtons } from '../../schedule-calendar-components/appointment-event-card/appointment-event-action-buttons';
import { useApptPanelContext } from './appt-panel-context';
import { InsuranceStatusIcon } from './insurance-status-icon';

export const PanelHeader = () => {
  const { t } = useTranslation('schedule');
  const { setPersonId, setSelectedTab } = useContactPanelShallowStore('setPersonId', 'setScrollTo', 'setSelectedTab');
  const { selectedAppointmentId, selectedAppointments, resetSelectedAppointment } =
    useScheduleAppointmentInfoPanelShallowStore(
      'selectedAppointmentId',
      'selectedAppointments',
      'resetSelectedAppointment'
    );
  const { apptDetails, locationId, personInfo, isLoadingPersonInfo } = useApptPanelContext();

  const personId = apptDetails?.personInfo.id || '';

  const handlePersonClick = () => {
    setPersonId(personId, true, locationId);
    setSelectedTab('overview-tab');
  };

  const apptId = selectedAppointmentId || '';
  const pmDisplayID = personInfo?.PMDisplayID || '';
  const personName = personInfo ? getFullName(personInfo) : apptDetails?.personInfo.name || '';
  const personStatus = personInfo?.Status || (apptDetails?.personInfo.status || '').toLowerCase();
  const personGender = personInfo?.Gender || apptDetails?.personInfo.gender || '';
  const personAge = personInfo?.Birthdate
    ? t('{{age}} yrs', { age: dayjs().diff(dayjs(personInfo?.Birthdate), 'year') })
    : apptDetails?.personInfo.age || '';
  const [personFirstWord, personRestWord] = personName.split(/\s(.*)/s);
  const genderText = ['Unknown', 'Other'].includes(personGender)
    ? t('{{gender}} gender', { gender: personGender })
    : personGender;

  return (
    <header css={headerStyle}>
      <div css={{ width: '100%', display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
        {selectedAppointments.length > 1 && (
          <Button variant='secondary' iconName='caret-left' onClick={resetSelectedAppointment} />
        )}
        <div css={[styles.flexCenter, { flex: 1, gap: theme.spacing(1) }]}>
          <Truncated
            as={Heading}
            level={2}
            css={{ cursor: 'pointer', fontWeight: 'normal', width: 'auto', maxWidth: 250 }}
            onClick={handlePersonClick}
          >
            <strong>{personFirstWord}</strong> {personRestWord}
          </Truncated>
          <InsuranceStatusIcon
            locationId={locationId}
            personId={personId}
            personInfo={personInfo}
            isLoading={isLoadingPersonInfo}
          />
        </div>
      </div>
      <div css={{ display: 'flex', gap: theme.spacing(2), justifyContent: 'center' }}>
        {!!pmDisplayID && (
          <Truncated
            as={CopyableText}
            size='small'
            color='subdued'
            css={{ maxWidth: 50 }}
            tooltipSuccessText={t('ID copied')}
            tooltipText={t('Copy ID')}
          >
            #{pmDisplayID}
          </Truncated>
        )}
        {!!personStatus && personStatus !== 'Active' && <InfoText text={personStatus} />}
        {genderText && <InfoText text={genderText} />}
        {personAge && <InfoText text={personAge} />}
      </div>
      <AppointmentEventActionButtons
        locationId={locationId}
        personId={personId}
        appointmentId={apptId}
        hideEditApptAction
      />
    </header>
  );
};

const InfoText = ({ text }: { text: string }) =>
  text ? (
    <Text color='subdued' size='small' css={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
      {text}
    </Text>
  ) : null;

const headerStyle = css({
  borderBottom: `1px solid ${theme.colors.neutral10}`,
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
});
